import { useContext, useState, useEffect } from "react";
import { Navigate, Routes, Route } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { ScaleLoader } from "react-spinners";
import { auth } from "./database/db";
import DashboardLayout from "./layouts/dashboard";
import DashboardApp from "./pages/DashboardApp";
import Login from "./pages/Login";
import NotFound from "./pages/Page404";

// Admin Management
import AddAdmin from "./pages/AddAdmin";
import AdminList from "./pages/AdminList";

// User Management
import UserManagement from "./pages/UserManagement";
import UserView from "./pages/UserView";

// Reports / Community
import ReportedUsers from "./pages/ReportedUsers";

// FIV Monitor
import FIVMonitor from "./pages/FIVMonitor";
import TopFIV from "./pages/TopFIV";

// Notifications & DM
import MassNotification from "./pages/MassNotification";
import DMCenter from "./pages/DMCenter";

// Token & Gifts
import TokenGifts from "./pages/TokenGifts";
import GiftList from "./pages/GiftList";
import GiftDetails from "./pages/GiftDetails";
import UpdateGift from "./pages/UpdateGift";
import TokenSettings from "./pages/TokenSettings";

// Money Requests
import PendingRequest from "./pages/moneyrequest/PendingRequest";
import AcceptRequest from "./pages/moneyrequest/AcceptRequest";
import DeclineRequest from "./pages/moneyrequest/DeclineRequest";

// New modules
import AuditLog from "./pages/AuditLog";
import ContentModeration from "./pages/ContentModeration";
import RevenueCharts from "./pages/RevenueCharts";
import SubscriptionManagement from "./pages/SubscriptionManagement";
import AppSettings from "./pages/AppSettings";
import PayoutTracker from "./pages/PayoutTracker";
import StreamMonitor from "./pages/StreamMonitor";
import BrokerageHealth from "./pages/BrokerageHealth";

// Other
import CreatorsList from "./pages/CreatorsList";
import CreatorRequest from "./pages/CreatorRequest";
import CreatorRequestView from "./pages/CreatorRequestView";
import Ledger from "./pages/Ledger";
import Support from "./pages/Support";
import UpdatePassword from "./pages/UpdatePassword";
import ViewImage from "./pages/ViewImage";

import { AuthContext } from "./Context/authContext";

function PrivateRoute({ children }) {
  const [cachedAuth] = useContext(AuthContext);

  // Firebase's own session restore (reading the persisted login out of
  // IndexedDB) is async and can take a beat after a fresh page load/reload.
  // AuthContext's `cachedAuth` comes from localStorage and is available
  // instantly, which used to let pages mount (and fire Firestore
  // onSnapshot/getDocs calls) before firebase Auth's `currentUser` was
  // actually populated. Those calls were evaluated server-side with
  // request.auth == null, got a terminal permission-denied, and never
  // retried even once Firebase auth caught up a moment later. Waiting for
  // onAuthStateChanged's first callback before rendering children closes
  // that race for every admin page at once.
  const [firebaseUser, setFirebaseUser] = useState(undefined); // undefined = not yet known

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => setFirebaseUser(user));
    return () => unsub();
  }, []);

  if (!cachedAuth) return <Navigate to="/login" replace />;

  if (firebaseUser === undefined) {
    // Waiting on Firebase's real session check — render nothing functional
    // yet so child pages can't fire Firestore calls before auth is ready.
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", background: "#0e0e0e" }}>
        <ScaleLoader color="#FF6C40" />
      </div>
    );
  }

  if (!firebaseUser) {
    // Firebase says there's no real session even though a stale "auth" flag
    // was left in localStorage (e.g. token revoked/expired elsewhere) — log
    // out cleanly instead of looping permission-denied errors forever.
    localStorage.removeItem("auth");
    return <Navigate to="/login" replace />;
  }

  return children;
}

export default function Router() {
  return (
    <Routes>
      <Route path="/" element={<PrivateRoute><DashboardLayout /></PrivateRoute>}>
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardApp />} />

        {/* Administrator */}
        <Route path="addadmin" element={<AddAdmin />} />
        <Route path="adminlist" element={<AdminList />} />
        <Route path="updateadmin" element={<AddAdmin update={true} />} />
        <Route path="updateaccount" element={<AddAdmin update={true} account={true} />} />

        {/* User Management */}
        <Route path="users" element={<UserManagement />} />
        <Route path="viewuser" element={<UserView />} />

        {/* Community Reports */}
        <Route path="reported" element={<ReportedUsers />} />

        {/* FIV */}
        <Route path="fivmonitor" element={<FIVMonitor />} />
        <Route path="topfiv" element={<TopFIV />} />

        {/* Notifications & DM */}
        <Route path="mass" element={<MassNotification />} />
        <Route path="dm" element={<DMCenter />} />

        {/* Token & Gifts */}
        <Route path="tokengifts" element={<TokenGifts />} />
        <Route path="giftlist" element={<GiftList />} />
        <Route path="giftdetails" element={<GiftDetails />} />
        <Route path="updategift" element={<UpdateGift />} />
        <Route path="tokensettings" element={<TokenSettings />} />

        {/* Money Requests */}
        <Route path="pendingmoneyrequest" element={<PendingRequest />} />
        <Route path="acceptmoneyrequest" element={<AcceptRequest />} />
        <Route path="declinemoneyrequest" element={<DeclineRequest />} />

        {/* Creators */}
        <Route path="creatorslist" element={<CreatorsList />} />
        <Route path="creatorrequest" element={<CreatorRequest />} />
        <Route path="creatorrequestView" element={<CreatorRequestView />} />

        {/* Misc */}
        <Route path="ledger" element={<Ledger />} />
        <Route path="support" element={<Support />} />
        <Route path="updatepassword" element={<UpdatePassword />} />
        <Route path="viewimage" element={<ViewImage />} />

        {/* New modules */}
        <Route path="auditlog" element={<AuditLog />} />
        <Route path="contentmoderation" element={<ContentModeration />} />
        <Route path="revenuecharts" element={<RevenueCharts />} />
        <Route path="subscriptions" element={<SubscriptionManagement />} />
        <Route path="appsettings" element={<AppSettings />} />
        <Route path="payouttracker" element={<PayoutTracker />} />
        <Route path="streammonitor" element={<StreamMonitor />} />
        <Route path="brokeragehealth" element={<BrokerageHealth />} />

        {/* Legacy routes redirect */}
        <Route path="userlist" element={<Navigate to="/users" replace />} />
        <Route path="reports" element={<Navigate to="/reported" replace />} />
      </Route>
      <Route path="/login" element={<Login />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
